import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PercentIcon from "@mui/icons-material/Percent";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { v4 } from "uuid";
import { Link } from "react-router-dom";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { appSettingsRepository } from "../app-settings.repository";
import { updateBussinessDisplayName } from "../../bussiness/update-bussiness-name";
import { cashWithdrawalCategoryRepository } from "../../cash-register/cash-withdrawal-category.repository";
import { CashWithdrawalCategory } from "../../cash-register/cash-withdrawal-category.entity";
import { useModuleAccess } from "../module-access";
import { useCashRegister } from "../../cash-register/cash-register-context";

export const SettingsPage = () => {
  application.useHasPermission(Permission.ADMIN);
  const [bussiness] = application.bussiness.useState();
  const [appSettings, setAppSettings] = useState(() => application.appSettings.get());
  const [businessName, setBusinessName] = useState(() => application.bussiness.get()?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const moduleAccess = useModuleAccess();
  const cashRegisterModuleActive = moduleAccess.isEnabled("cash_register");
  const { session: openCashSession } = useCashRegister();
  const hasOpenSession = !!openCashSession;

  const [categories, setCategories] = useState<CashWithdrawalCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CashWithdrawalCategory | null>(null);

  useEffect(() => {
    if (!cashRegisterModuleActive) return;
    setLoadingCategories(true);
    cashWithdrawalCategoryRepository
      .listAll()
      .then(setCategories)
      .catch(() => setCategories([]))
      .finally(() => setLoadingCategories(false));
  }, [cashRegisterModuleActive]);

  const saveAll = useCallback(async () => {
    if (!bussiness) return;
    setSaving(true);
    try {
      await appSettingsRepository.saveConfig(appSettings);
      application.appSettings.set(appSettings);
      const trimmed = businessName.trim();
      if (trimmed && trimmed !== bussiness.name) {
        const updated = await updateBussinessDisplayName(bussiness, trimmed);
        application.bussiness.set(updated);
      }
      setToast({ open: true, message: "Configurações salvas com sucesso.", severity: "success" });
    } catch (e) {
      setToast({
        open: true,
        message: e instanceof Error ? e.message : "Não foi possível salvar as alterações.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [appSettings, businessName, bussiness]);

  const handleAddCategory = async () => {
    const title = newCategoryName.trim();
    if (!title) return;
    setAddingCategory(true);
    try {
      const cat = new CashWithdrawalCategory(v4(), title);
      await cashWithdrawalCategoryRepository.save(cat);
      setCategories((prev) => [...prev, cat].sort((a, b) => a.getTitle().localeCompare(b.getTitle())));
      setNewCategoryName("");
    } catch {
      setToast({ open: true, message: "Não foi possível salvar a categoria.", severity: "error" });
    } finally {
      setAddingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteTarget) return;
    try {
      await cashWithdrawalCategoryRepository.remove(deleteTarget);
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch {
      setToast({ open: true, message: "Não foi possível remover a categoria.", severity: "error" });
    } finally {
      setDeleteTarget(null);
    }
  };

  if (!bussiness) {
    return (
      <Shell title="Configurações">
        <LoadingOverlay message="Carregando dados do negócio..." />
      </Shell>
    );
  }

  return (
    <Shell title="Configurações">
      {saving ? <LoadingOverlay subtle message="Salvando..." /> : null}

      <PageHeader
        title="Configurações"
        description="Preferências gerais do negócio e do aplicativo. Assinatura, módulos, cartão e créditos ficam em Meu Plano."
      />

      <Stack spacing={3}>
        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "success.main",
                  color: "success.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PercentIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>Comissões</Typography>
                <Typography variant="body2" color="text.secondary">
                  Percentual padrão de comissão para vendas.
                </Typography>
              </Box>
            </Stack>
            <TextField
              label="Comissão (%)"
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              value={appSettings.commissionPercent}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setAppSettings((prev) => prev.withCommissionPercent(Number.isNaN(value) ? 0 : value));
              }}
              fullWidth
              sx={{ maxWidth: 280 }}
            />
          </CardContent>
        </Card>

        {cashRegisterModuleActive && (
          <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PointOfSaleIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Fluxo de caixa</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controle de abertura, fechamento, entradas e retiradas.
                  </Typography>
                </Box>
              </Stack>

              <FormControlLabel
                sx={{ mb: appSettings.cashRegisterDisabled ? 0 : 3 }}
                control={
                  <Switch
                    checked={!appSettings.cashRegisterDisabled}
                    disabled={hasOpenSession && !appSettings.cashRegisterDisabled}
                    onChange={(e) =>
                      setAppSettings((prev) => prev.withCashRegisterDisabled(!e.target.checked))
                    }
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Controle de caixa ativo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Desative para ocultar o caixa de toda a interface do sistema.
                    </Typography>
                  </Box>
                }
              />

              {hasOpenSession && !appSettings.cashRegisterDisabled && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3, borderRadius: 2 }}
                  action={
                    <Button
                      color="warning"
                      size="small"
                      component={Link}
                      to="/caixa"
                      sx={{ fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" }}
                    >
                      Ir para o caixa
                    </Button>
                  }
                >
                  Há um caixa aberto. Feche-o antes de desativar o controle de caixa.
                </Alert>
              )}

              {!appSettings.cashRegisterDisabled && (
                <>
                  <Divider sx={{ mb: 2.5 }} />
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <LabelOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Categorias de retirada
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (a categoria "Pagar conta" é fixa e sempre aparece)
                    </Typography>
                  </Stack>

                  {loadingCategories ? (
                    <Typography variant="body2" color="text.secondary">Carregando…</Typography>
                  ) : (
                    <>
                      {categories.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Nenhuma categoria configurada ainda.
                        </Typography>
                      ) : (
                        <List disablePadding sx={{ mb: 1.5 }}>
                          {categories.map((cat) => (
                            <ListItem
                              key={cat.id}
                              disableGutters
                              secondaryAction={
                                <IconButton
                                  size="small"
                                  edge="end"
                                  onClick={() => setDeleteTarget(cat)}
                                  sx={{ color: "error.main" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              }
                              sx={{
                                py: 0.5,
                                px: 1.5,
                                borderRadius: 2,
                                "&:hover": { bgcolor: (t) => alpha(t.palette.action.hover, 0.06) },
                              }}
                            >
                              <ListItemText
                                primary={cat.getTitle()}
                                primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}

                      <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 360 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Nova categoria"
                          placeholder="Ex.: Despesa operacional, Retirada do sócio…"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") void handleAddCategory(); }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => void handleAddCategory()}
                          disabled={addingCategory || !newCategoryName.trim()}
                          sx={{ whiteSpace: "nowrap", fontWeight: 700, textTransform: "none" }}
                        >
                          Adicionar
                        </Button>
                      </Stack>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <StorefrontIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>Negócio</Typography>
                <Typography variant="body2" color="text.secondary">
                  Dados básicos exibidos no painel.
                </Typography>
              </Box>
            </Stack>
            <TextField
              label="Nome do negócio"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip size="small" variant="outlined" label={`Chave: ${bussiness.key}`} />
            </Stack>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          size="large"
          startIcon={<SettingsSuggestIcon />}
          onClick={() => void saveAll()}
          sx={{ alignSelf: "flex-start" }}
        >
          Salvar alterações
        </Button>
      </Stack>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Remover categoria</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deseja remover a categoria <strong>{deleteTarget?.getTitle()}</strong>?
            Retiradas já registradas com ela não serão afetadas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDeleteCategory()}
            sx={{ fontWeight: 800 }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((current) => ({ ...current, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Shell>
  );
};
